/**
  * Centered floating alerts
  */
// This is in block scope because it gets concatenated into LA Widgets, so we need the class def to be non-global in case it gets loaded twice
// Once we move to all imports/modules/builds we could remove that.
class Notify {
    #parent = null;
    #defaultParentId = 's-ui-notifyarea';
    #defaultAlertId = 's-ui-notify';
    #defaultClassname = 's-ui-notify';
    #workingConfig = {
        msg: '<i class="fa fa-spinner fa-pulse mg-right" aria-hidden="true"></i> <span>Working...</span>',
        className: 's-ui-notify-working'
    };

    #successConfig = {
        msg: 'Success.',
        className: 's-ui-notify-success'
    };

    #errorConfig = {
        msg: 'Error: Please try again.',
        className: 's-ui-notify-error'
    };

    #idCount = 0;
    #boundKeyboardEvents = null;

    constructor({
        parent_id = null
    }) {
        // Its best for screenreaders if the parent is already in the DOM.
        if (parent_id) {
            this.#defaultParentId = parent_id;
        }
    }

    #setUpParent() {
        let parent = document.getElementById(this.#defaultParentId);
        if (!parent) {
            parent = document.createElement('DIV');
            parent.id = this.#defaultParentId;
            parent.setAttribute('role', 'alert');
            parent.setAttribute('aria-atomic', 'true');
            document.body.appendChild(parent);
        }
        return parent;
    }

    /**
     * @returns {HTMLElement}
     */
    #getParent() {
        if (!this.#parent) {
            this.#parent = this.#setUpParent();
        }
        return this.#parent;
    }

    /**
         * A default error alert
         * @param {String} [msg] Custom error message
         * @param {String} [id] Alert id to replace, else new alert created.
         */
    error(msg = '', id = null) {
        const config = { ...this.#errorConfig };
        if (msg) {
            config.msg = msg;
        }
        config.msg = `<i class="fa fa-exclamation-triangle mg-right" aria-hidden="true"></i> ${config.msg}`;
        config.id = id;
        this.show(config);
    }

    /**
         * A default success alert
         * @param {String} [msg] Custom success message
         * @param {String} [id] Alert id to replace, else new alert created.
         * @param {Number} [ms] Timeout in milliseconds
         */
    success(msg = '', id = null, ms = 0) {
        const config = { ...this.#successConfig };
        if (msg) {
            config.msg = msg;
        }
        if (ms > 0) {
            config.ms = ms;
        }
        config.msg = `<i class="fa fa-check-circle mg-right" aria-hidden="true"></i> ${config.msg}`;
        config.id = id;
        this.show(config);
    }

    /**
         * default "working" alert
         * @param {String} [msg] custom message
         * @param {String} [id] Alert id to replace, else new alert created.
         */
    working(msg = '', id = null) {
        const config = { ...this.#workingConfig };
        if (msg) {
            config.msg = msg;
        }
        config.id = id;
        this.show(config);
    }

    /**
         * Creates/displays an alert
         * @param {Object} settings Alert config
         * @param {String} settings.msg message to display
         * @param {String} [settings.className=s-ui-notification] custom class to attach to alert (for styling)
         * @param {String} [settings.id] Id for alert
         * @param {Number} [settings.ms] Timeout in ms
         * @returns {String} alert id
         */
    show({
        msg = '',
        className = null,
        id = null,
        ms = 0
    }) {
        if (!msg) {
            return null;
        }
        let el = null;
        if (!id) {
            this.#idCount++;
            id = `${this.#defaultAlertId}-${this.#idCount}`;
        } else {
            el = document.getElementById(id);
            if (el) {
                el.remove();
            }
        }
        el = document.createElement('DIV');
        el.id = id;
        el.classList.add(this.#defaultClassname);
        if (className) {
            el.classList.add(className);
        }
        el.innerHTML = `<div class="s-ui-notification-message">${msg}</div><button class="btn" aria-label="${window?.springyText?.modal?.close || 'Close'}">&times;</button>`;

        const parent = this.#getParent();
        parent.appendChild(el);
        el.addEventListener('click', (ev) => {
            ev.preventDefault();
            this.hide(ev.currentTarget);
        });
        if (ms > 0) {
            setTimeout(() => {
                this.hide(el);
            }, ms);
        }
        // add a close keyboard action
        if (this.#boundKeyboardEvents === null) {
            this.#boundKeyboardEvents = this.keyboardEvents.bind(this);
            document.addEventListener('keydown', this.#boundKeyboardEvents, true);
        }
        return id;
    }

    /**
         * Hide a notification or all notifications
         * @param {HTMLElement|null} [alert=null] Alert to hide (else all)
         */
    hide(alert = null) {
        if (alert) {
            alert.remove();
        } else if (this.#parent) {
            this.#parent.innerHTML = '';
        }
        this.#removeKeyboardEvents();
    }

    /**
     * Hide any working alerts.
     */
    hideWorking() {
        if (!this.#parent) {
            return;
        }
        this.#parent.querySelectorAll(`.${this.#workingConfig.className}`).forEach((el) => {
            el.remove();
        });
        this.#removeKeyboardEvents();
    }

    /**
     * If there are no more alerts, turn off the keyboard close event
     */
    #removeKeyboardEvents() {
        if (this.#boundKeyboardEvents === null ||
            !this.#parent ||
            this.#parent.children.length > 0) {
            return;
        }
        document.removeEventListener('keydown', this.#boundKeyboardEvents, true);
        this.#boundKeyboardEvents = null;
    }

    /**
     * Handler: Keyboard event to:
     * - Close on Esc
     * @param {KeyboardEvent} ev Keydown event
     */
    keyboardEvents(ev) {
        if (ev.key !== 'Escape') {
            return;
        }
        // close the first/oldest alert first
        const firstAlert = this.#parent.querySelector(`.${this.#defaultClassname}`);
        if (firstAlert) {
            firstAlert.remove();
        }
        this.#removeKeyboardEvents();
    }
}

export default new Notify({});
